import React, {createContext, ReactNode, useCallback, useEffect, useState} from "react";
import {AppMetadataContextType} from "../models/interfaces/app-metadata-context-type.interface";
import {AppMetadataDto} from "../models/interfaces/app-metadata-dto.interface";
import {FileDto} from "../models/interfaces/file-dto.interface";
import {useAppMetadataApi} from "../hooks/use-app-metadata-api";
import {useLoading} from "../hooks/use-loading.hook";
import {LoadingKeys} from "../models/enums/loading-keys.enum";
import Base64Helper from "../../../utils/base64-helper";
import {forkJoin} from "@libs/axios";
import {DuringVisitInfo} from "../models/interfaces/during-visit-info.interface";

export const AppMetadataContext = createContext<AppMetadataContextType | null>(null);

interface AppMetadataProviderProps {
    children: ReactNode;
}

export const AppMetadataProvider: React.FC<AppMetadataProviderProps> = ({children}) => {
    const [appMetadata, setAppMetadata] = useState<AppMetadataDto | null>(null);
    const [duringVisitInfo, setDuringVisitInfo] = useState<DuringVisitInfo | null>(null); // dodaj odpowiedni typ
    const [widgetImage, setWidgetImage] = useState<FileDto | null>(null);
    const {
        getAppMetadata,
        getDuringVisitInfo,
        updateTheming: updateThemingApi,
        toggleVisibility: toggleVisibilityApi,
        getWidgetImage: getWidgetImageApi,
        uploadWidgetImage: uploadWidgetImageApi,
        updateRequiredDuringVisit: updateRequiredDuringVisitApi,
        updateOptionalDuringVisit: updateOptionalDuringVisitApi,
    } = useAppMetadataApi();
    const {isLoading} = useLoading();

    const fetchAppMetadata = useCallback(async (): Promise<void> => {
        try {
            const {metadata, duringVisitInfo} = await forkJoin({
                metadata: getAppMetadata(),
                duringVisitInfo: getDuringVisitInfo(),
            });

            setAppMetadata(metadata);
            setDuringVisitInfo(duringVisitInfo);

            if (metadata.widgetImageFileId) {
                const {image} = await forkJoin({
                    image: getWidgetImageApi(),
                });
                const mimeType = image.extension || 'image/png';
                setWidgetImage({
                    ...image,
                    base64: image.base64 ? Base64Helper.addBase64Prefix(image.base64, mimeType) : null,
                });
            } else {
                setWidgetImage(null);
            }
        } catch (error) {
            console.error("Error fetching app metadata:", error);
        }
    }, [getAppMetadata, getDuringVisitInfo, getWidgetImageApi]);

    const updateTheming = useCallback(async (theming: string): Promise<void> => {
        try {
            await updateThemingApi({theming});
            setAppMetadata(prev => prev ? ({...prev, theming}) : prev);
        } catch (error) {
            console.error("Error updating theming:", error);
            throw error;
        }
    }, []);

    const updateRequiredDuringVisit = useCallback(async (text: string): Promise<void> => {
        try {
            await updateRequiredDuringVisitApi({text});
            setDuringVisitInfo(prev => prev ? ({...prev, requiredDuringVisit: text}) : prev);
        } catch (error) {
            console.error("Error updating required during visit:", error);
            throw error;
        }
    }, [updateRequiredDuringVisitApi]);

    const updateOptionalDuringVisit = useCallback(async (text: string): Promise<void> => {
        try {
            await updateOptionalDuringVisitApi({text});
            setDuringVisitInfo(prev => prev ? ({...prev, optionalDuringVisit: text}) : prev);
        } catch (error) {
            console.error("Error updating optional during visit:", error);
            throw error;
        }
    }, [updateOptionalDuringVisitApi]);

    const toggleVisibility = useCallback(async (): Promise<void> => {
        try {
            await toggleVisibilityApi();
            setAppMetadata(prev => prev ? ({...prev, hidden: !prev.hidden}) : prev);
        } catch (error) {
            console.error("Error toggling visibility:", error);
            throw error;
        }
    }, []);

    const getWidgetImage = useCallback(async (): Promise<FileDto> => {
        try {
            return await getWidgetImageApi();
        } catch (error) {
            console.error("Error getting widget image:", error);
            throw error;
        }
    }, [getWidgetImageApi]);

    const uploadWidgetImage = useCallback(async (file: File): Promise<void> => {
        try {
            await uploadWidgetImageApi(file);
            await fetchAppMetadata();
        } catch (error) {
            console.error("Error uploading widget image:", error);
            throw error;
        }
    }, [uploadWidgetImageApi, fetchAppMetadata]);

    useEffect(() => {
        void fetchAppMetadata();
    }, []);

    const loading = isLoading([LoadingKeys.USE_APP_METADATA_API, "getAppMetadata"]) ||
        isLoading([LoadingKeys.USE_APP_METADATA_API, "updateTheming"]);

    const value: AppMetadataContextType = {
        appMetadata,
        widgetImage,
        loading,
        error: null,
        fetchAppMetadata,
        updateTheming,
        toggleVisibility,
        getWidgetImage,
        uploadWidgetImage,
        duringVisitInfo,
        updateRequiredDuringVisit,
        updateOptionalDuringVisit,
    };

    return (
        <AppMetadataContext.Provider value={value}>
            {children}
        </AppMetadataContext.Provider>
    );
};

